import SelectViewingGroups from "@components/auto-completes/viewing-groups";
import {
  SHHtmlBlock,
  SHLabel,
  SHSelect,
  SHStack,
  SHTypography,
} from "@components/design-systems";
import {
  useEditModelPaths,
  useInputDotIndicator,
  useUpdateSectionData,
} from "@hooks/useInvestmentProduct";
import { useUserPermissions } from "@hooks/userUserPermission";
import { HighlightType } from "@models/configuration";
import { ProductDataStatus } from "@models/product/enums/productDataStatus";
import { InvestmentProductConfigurationFeature } from "@models/product/investment-product/entities/investmentProduct";
import { InvestmentProductModelsDTO } from "@models/product/investment-product/entities/model";
import { InvestmentBannerStatus } from "@models/product/investment-product/enums/status";
import { CustomLabel } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/custom-label";
import { DEBOUNCE_DELAY } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/model/configs";
import { castToViewingGroupSelectionDTO } from "@pages/suppliers/_id/_products/investment-product/_id/edit/components/tabs/model/utils";
import { RootState } from "@redux/store";
import { hexToRGBA } from "@utils";
import { debounce, defaultTo, isEmpty, isEqual, isNil } from "lodash";
import { useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { FeatureOnlyAdminCanUpdateData } from "@pages/suppliers/_id/_products/investment-product/_id/edit/constants";
interface ModelFeatureDropdownProps {
  selectedIndex: [number, number];
  sectionIndex: number;
  featureIndex: number;
  feature: InvestmentProductConfigurationFeature;
}

export const ModelFeatureDropdown = ({
  selectedIndex,
  sectionIndex,
  featureIndex,
  feature,
}: ModelFeatureDropdownProps) => {
  const {
    investmentProductUI: { isSaving },
    investmentProductBannerInfo,
  } = useSelector((state: RootState) => state.investmentProduct);
  const { isAdminGroup } = useUserPermissions();
  const originalValue = feature.productData?.value;
  const isPendingApproval = investmentProductBannerInfo?.some((banner) =>
    isEqual(banner.status, InvestmentBannerStatus.PendingApproval),
  );
  /** Hook */
  const { control, setValue, getValues } = useFormContext<{
    investmentProductModels: InvestmentProductModelsDTO[];
  }>();
  const { featureProductDataPath, featurePath } = useEditModelPaths(
    selectedIndex,
    sectionIndex,
    featureIndex,
  );
  const watchedValue = useWatch({
    control,
    name: [featureProductDataPath.value],
  });
  const defaultValue =
    useMemo(
      () => feature.lovData?.find((opt) => opt.isDefault)?.id,
      [feature.lovData],
    ) ?? null;
  const productDataValue = useMemo(() => {
    const value = getValues(featureProductDataPath.value);
    if (isNil(value) && !isNil(defaultValue)) return defaultValue;
    return value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedValue, featureIndex, selectedIndex, sectionIndex]);
  const { isShowOrangeDot, isShowBlueDot } = useInputDotIndicator<{
    investmentProductModels: InvestmentProductModelsDTO[];
  }>({
    getValues,
    control,
    paths: {
      highlightType: featureProductDataPath.highlightType,
      dataStatus: featureProductDataPath.dataStatus,
    },
  });
  const handleUpdateSectionData = useUpdateSectionData(
    selectedIndex,
    sectionIndex,
  );
  /** State */

  /** Handler */
  const handleInputChange = (newValue: string | null) => {
    setValue(
      featureProductDataPath.dataStatus,
      isEmpty(newValue)
        ? ProductDataStatus.MissingData
        : ProductDataStatus.Filled,
    );
    setValue(
      featurePath.dataStatus,
      isEmpty(newValue)
        ? ProductDataStatus.MissingData
        : ProductDataStatus.Filled,
    );
    setValue(featurePath.isModified, !isEqual(newValue, originalValue));
    setValue(
      featureProductDataPath.isModified,
      !isEqual(newValue, originalValue),
    );
    const publishedValue = defaultTo(
      getValues(featureProductDataPath.publishedValue),
      "",
    );

    if (!isEqual(publishedValue, newValue)) {
      setValue(featureProductDataPath.highlightType, HighlightType.Edited);
      setValue(featurePath.highlightType, HighlightType.Edited);
    } else {
      setValue(featureProductDataPath.highlightType, null);
      setValue(featurePath.highlightType, null);
    }
    handleUpdateSectionData();
  };
  const debouncedHandleInputChange = debounce(
    handleInputChange,
    DEBOUNCE_DELAY,
  );

  const isDisable = useMemo(() => {
    const isRestrictedEditingFeature =
      !isAdminGroup &&
      FeatureOnlyAdminCanUpdateData.includes(feature.name ?? ""); //TODO: hard-code to disable feature (SD-5376)
    return isSaving || isPendingApproval || isRestrictedEditingFeature;
  }, [feature, isAdminGroup, isSaving, isPendingApproval]);

  /** Side effect */

  return (
    <>
      <SHStack direction="column" gap={2} width={"100%"}>
        <SHStack>
          <SHLabel
            label={
              <CustomLabel
                featureData={feature}
                disableInformationButton
                dotProps={{
                  orangeDot: isShowOrangeDot,
                  blueDot: isShowBlueDot,
                  greenDot: feature.isBusinessMetric,
                }}
              />
            }
          />
          <SHHtmlBlock variant="body3" content={feature.description} />
        </SHStack>
        {feature.allowedVisibilitySetting && (
          <SHStack>
            <SHLabel
              label={
                <SHTypography variant="subtitle2">Visible to</SHTypography>
              }
            />
            <Controller
              name={featurePath.viewingGroupSelections}
              control={control}
              render={({ field: { ref, ...other } }) => (
                <SelectViewingGroups
                  disabled={isDisable}
                  viewingGroupData={feature.viewingGroupSelections ?? []}
                  value={
                    castToViewingGroupSelectionDTO(
                      getValues(featurePath.viewingGroupSelections),
                    ) ?? []
                  }
                  onChange={(value) => {
                    other.onChange(value);
                    setValue(
                      featurePath.isModified,
                      !isEqual(feature.viewingGroupSelections, value),
                    );
                    handleUpdateSectionData();
                  }}
                />
              )}
            />
          </SHStack>
        )}
      </SHStack>
      <SHStack>
        <Controller
          control={control}
          name={featureProductDataPath["value"]}
          render={({ field: { ref, ...other } }) => (
            <SHSelect
              {...other}
              dataItemKey="id"
              displayField="name"
              placeholder="Select"
              disabled={isDisable}
              data={feature.lovData ?? []}
              value={productDataValue}
              sx={{
                "& .sh-select-placeholder": {
                  color: `${hexToRGBA("#3D98FF", 0.6)} !important`,
                },
                width: "520px",
                flex: 1,
              }}
              allowClear={!defaultValue && !!other.value}
              onClear={(value) => {
                other.onChange(value);
                debouncedHandleInputChange(value ?? "");
              }}
              onChange={(event) => {
                const newValue = event.target.value;
                other.onChange(newValue);
                debouncedHandleInputChange(newValue);
              }}
            />
          )}
        />
      </SHStack>
    </>
  );
};
